import { gridMap } from '../mapgrid.ts';
import { withGeometry } from '../mapgeo.ts';
import { make, poly, type MapDoor, type MapPoly, type MapRoof } from '../geom.ts';

/**
 * Wasteland: a settlers' town years after the fall. A collapsed overpass and its twin, a crashed airliner broken in two, a
 * toppled water tower, a gas station with its canopy half down, a shanty town, a bunker, a dry pool, an overgrown church and a
 * radiation crater, in nine districts. The west half of the grid is drawn here (^ broken concrete, ~ scrap sheet); polygons,
 * doors and roofs are listed once and withGeometry turns each half a turn, so the two halves are different places made of the
 * same bones: the overpass and the airliner, the gas station and the market, the shanty and the bunker, the pool and the church.
 */
const WEST = `
  ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
  ^...........................................................
  ^...........................................................
  ^.....................^^^^^^................................
  ^..........................^................................
  ^.............~............^...^............................
  ^.....RRRRRRR.~.......^....^...^............................
  ^.....RRRRRRR.~.......^^..^^...^............................
  ^.....RRRRRRR.~................^............................
  ^.....RRRRRRR.~.............................................
  ^.....RRRRRRR.~................^^^..........................
  ^.....RRRRRRR.~.............................................
  ^.....RRRRRRR.~.............................................
  ^.............~..............l..............................
  ^.....~~~~~~~~~~~.FFF.....c................~~~~..~~~~.......
  ^.................FFF.................o....~...e....~.......
  ^..........c......FFF......................~~.......~.......
  ^..........................................~~.............g.
  ^.....................................^....~~.m.............
  ^....FFF.........................FFF..^.............~.......
  ^....FFF.........................FFF..^.............~.......
  ^....FFF.........................FFF..^....~~~~~~~~~~.....g.
  ^.....................................^.....................
  ^.....................................^..........c..........
  ^...............................c................b........b.
  ^..^...................b................a...................
  ^..^..................................^.....................
  ^..^..................................^.....................
  ^..^..................................^................^^^^.
  ^..^......................A...........^.............l.......
  ^................................b..........................
  ^........^^^^^..............................................
  ^...........................................................
  ^.............a.............................................
  ^.......................................................FFF.
  ^...........c...........................................FFF.
  ^.......................................................FFF.
  ^...........................................................
  ^..................i........................................
  ^.....FFF...................................................
  ^.....FFF.........o..c...............................^^^.c..
  ^.....FFF...................................................
  ^...........................................................
  ^...........................................p...............
  ^.....................................FFF.............b.....
  ^.....................................FFF...................
  ^................^^^^..^^^^...........FFF...................
  ^............c...^........^......................l..........
  ^................^........^...............c.................
  ^.....................m...^.................................
  ^.........................^..............m..................
  ^................^........^.................................
  ^................^e.......^.........b.......^^^^............
  ^................^........^.................................
  ^..FFF....^^^^^^^^^..^^^^^^..^^^^.........FFF........p......
  ^..FFF....^.....................^.........FFF...............
  ^..FFF....^................b....^.........FFF...............
  ^.........^.....................^...........................
  ^.................l............a............................
  ^...........................................................
  ^.......................................e...................
  ^.............................o.............................
  ^.........^.............................^...................
  ^.........^.....................^.....c.^...................
  ^.........^.....................^.......^...................
  ^.........^..................p..^.......^...b...............
  ^......o..^^^^^..^^^^^^^..^^^^^^^.......^...................
  ^....................i.......................b..............
  ^...........................................................
  ^......i..................................................a.
  ^...............c.......................^^........c.........
  ^...........................................................
  ^..................FFF...........b.......FFF................
  ^..................FFF...................FFF....c...........
  ^..................FFF........^^^^l......FFF................
  ^............................................^^^^^^^^^......
  ^.......^^^...^^^............................^.......^......
  ^.......^.......^........................~...^.......^......
  ^.......^......o....^^..^^^..............~..................
  ^.......^...........^.....^...~~~~...~~~~~..................
  ^.......^.......^...^.....^..................^.......^..c...
  ^...FFF.^^^..^^^^............................^^^^^^^^^......
  ^...FFF.........................................o........^..
  ^...FFF.............^.....^......c.......................^..
  ^..........p........^^^^^^^.c.............i..............^..
  ^..~~~~~~..~~..~~~~~~.........................^^..^^^^...^..
  ^..~.......~........~...~~~~~~~~~~~...........^.............
  ^..~.......~............~.........~...........^.............
  ^..~....~..~......................~...........^......^......
  ^..~....~..~........~.............~...........^......^......
  ^..~~..~~..~........~...~.........~...........^^^^..^^......
  ^..........~~~...~~~~.b.~..~~..~~~~.........................
  ^........b...........e.....................FFF..............
  ^..........................................FFF..............
  ^..~~~~..~~~...............................FFF..............
  ^..~.......~...............~~~~~~~~~........................
  ^..~.......~......FFF......~.p.....~........................
  ^..~.m.....~......FFF......~.......~..............b.........
  ^..~.......~......FFF......~.......~....................g...
  ^..~...............................~.....~~~~~~~~~..........
  ^..~...............................~........................
  ^..~.......~...............~.......~........................
  ^..~.......~...............~.......~....................g...
  ^..~~~..~~~~.............l.~.......~.....~~~~~~~~~..........
  ^..........................~~..~~~~~........................
  ^......e.............................FFF....................
  ^....................................FFF....................
  ^..~~~...~~~~.............~~~~~..~~~.FFF....................
  ^..~........~.............~.p......~........................
  ^.........................~........~..FFF...................
  ^..............~~~...~~~..~...m....~..FFF...................
  ^..~........~..~.......~..~...........FFF...................
  ^..~........~..~.......~....................................
  ^..~........~..~.......~...........~................FFF.....
  ^..~........~..~.......~..~........~................FFF.....
  ^..~~~~~~~~~~..~.......~..~~~~~~~~~~................FFF.....
  ^..............~~~..~~~~....................................
  ^...........................................................
  ^...........................................................
  ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
`;

const P = (id: string, material: string, path: string, o: Partial<Omit<MapPoly, 'points' | 'material' | 'id'>> = {}): MapPoly => make(poly.fromPath(path), material, { id, ...o });
const roof = (id: string, path: string, material?: string): MapRoof => ({ id, points: poly.fromPath(path), ...(material && { material }) });

/** Wrecked vehicles share three hand-drawn outlines (nose toward +x), placed with a turn and a scale; the theme paints each by its shape. */
export const WRECKS = {
  car: poly.fromPath('-140,-52 -96,-62 -40,-62 -12,-74 52,-74 86,-60 140,-56 148,-20 144,36 130,58 70,62 -60,58 -130,54 -148,24 -150,-14'),
  bus: poly.fromPath('-385,-70 -360,-82 330,-82 372,-60 385,-20 385,40 372,70 -360,82 -385,60'),
  truck: poly.fromPath('-240,-80 120,-80 140,-84 210,-70 250,-30 250,50 230,80 -240,80'),
} as const;
const WRECK_HEIGHT = { car: 26, bus: 48, truck: 44 } as const;
const wreck = (id: string, shape: keyof typeof WRECKS, x: number, y: number, rot: number, scale = 1): MapPoly =>
  make(poly.transform(WRECKS[shape], { x, y, rot, scale }), 'wreck', { id, group: id, shape, height: WRECK_HEIGHT[shape] });

const POLYS: readonly MapPoly[] = [
  P('spanA-n0', 'span', '450,1370 720,1370 720,1410 450,1410', { height: 40, group: 'spanA', part: 'shell' }),
  P('spanA-n1', 'span', '840,1370 1130,1370 1130,1410 840,1410', { height: 40, group: 'spanA', part: 'shell' }),
  P('spanA-s2', 'span', '450,1590 1130,1590 1130,1630 450,1630', { height: 40, group: 'spanA', part: 'shell' }),
  P('spanA-cap', 'span', '450,1370 340,1409 300,1455 300,1545 340,1591 450,1630 450,1590 360,1569 340,1529 340,1471 360,1431 450,1410', { height: 40, group: 'spanA', part: 'cap' }),
  P('spanB-n0', 'span', '1540,1370 2150,1370 2150,1410 1540,1410', { height: 40, group: 'spanB', part: 'shell' }),
  P('spanB-s1', 'span', '1540,1590 1880,1590 1880,1630 1540,1630', { height: 40, group: 'spanB', part: 'shell' }),
  P('spanB-s2', 'span', '2000,1590 2150,1590 2150,1630 2000,1630', { height: 40, group: 'spanB', part: 'shell' }),
  P('spanB-cap', 'span', '2150,1370 2220,1428 2300,1460 2300,1540 2220,1572 2150,1630 2150,1590 2200,1548 2260,1524 2260,1476 2200,1452 2150,1410', { height: 40, group: 'spanB', part: 'cap' }),
  P('slabA-n', 'span', '790,1370 1060,1370 1250,940 1130,900', { height: 14, group: 'spanA', part: 'wing' }),
  P('slabA-s', 'span', '1050,1840 1410,1770 1400,2000 1130,2040', { height: 14, group: 'spanA', part: 'wing' }),
  P('slabB-n', 'span', '2110,1370 2270,1370 2350,1250 2260,1230', { height: 12, group: 'spanB', part: 'tailplane' }),
  P('slabB-s', 'span', '2110,1630 2270,1630 2350,1750 2260,1770', { height: 12, group: 'spanB', part: 'tailplane' }),
  P('pod', 'span', '640,1102 674,1116 688,1150 674,1184 640,1198 480,1198 446,1184 432,1150 446,1116 480,1102', { height: 24, group: 'pod', part: 'engine' }),
  P('billboard', 'sign', '1500,700 1920,700 1920,740 1500,740', { height: 70, blocksBullets: true }),
  P('bb-post-a', 'post', '1560,740 1596,740 1596,776 1560,776', { height: 30 }),
  P('bb-post-b', 'post', '1820,740 1856,740 1856,776 1820,776', { height: 30 }),
  P('gantry', 'sign', '300,2290 920,2260 925,2300 305,2330', { height: 26, group: 'gantry' }),
  P('tower-tank', 'frame', '797,2160 770,2221 718,2264 654,2280 588,2267 535,2226 505,2167 503,2100 530,2039 582,1996 646,1980 712,1993 765,2034 795,2093', { height: 52, group: 'tower', part: 'tank' }),
  P('tower-frame', 'frame', '780,2040 1560,2090 1562,2230 780,2220', { height: 34, group: 'tower', part: 'frame' }),
  P('tower-leg', 'frame', '1571,2022 1599,2050 1588,2089 1549,2098 1269,2018 1241,1990 1252,1951 1291,1942', { height: 30, group: 'tower', part: 'leg' }),
  wreck('car1', 'car', 2150, 1760, 0.5),
  wreck('car2', 'car', 2330, 1850, -0.35),
  wreck('car3', 'car', 1900, 2060, 0.3, 0.95),
  wreck('car5', 'car', 640, 2480, 0.1),
  wreck('car6', 'car', 1730, 2470, -0.9),
  wreck('bus', 'bus', 1300, 3900, 0.07),
  P('gs-pillar0', 'post', '2730,800 2721,821 2700,830 2679,821 2670,800 2679,779 2700,770 2721,779', { height: 44 }),
  P('gs-pillar1', 'post', '3430,1080 3421,1101 3400,1110 3379,1101 3370,1080 3379,1059 3400,1050 3421,1059', { height: 44 }),
  P('gs-pillar2', 'post', '2730,1080 2721,1101 2700,1110 2679,1101 2670,1080 2679,1059 2700,1050 2721,1059', { height: 44 }),
  P('gs-fallen', 'span', '3190,640 3480,700 3440,930 3270,900', { height: 18, group: 'gs-canopy', part: 'fallen' }),
  P('gs-island-a', 'low', '2900,920 2950,920 2950,1040 2900,1040', { height: 14, blocksBullets: false }),
  P('gs-island-b', 'low', '3150,920 3200,920 3200,1040 3150,1040', { height: 14, blocksBullets: false }),
  wreck('gs-car1', 'car', 3330, 1210, 0.3),
  wreck('gs-car2', 'car', 3480, 1290, -0.5),
  P('gs-sign', 'post', '2120,1200 2190,1200 2190,1270 2120,1270', { height: 60 }),
  P('sh-well', 'ruin', '5055,950 5044,982 5017,1002 4983,1002 4956,982 4945,950 4956,918 4983,898 5017,898 5044,918', { height: 22 }),
  P('apse', 'ruin', '1650,2800 1750,2780 1860,2860 1880,3000 1860,3140 1750,3220 1650,3200 1650,3150 1650,2850', { height: 40, group: 'hall', part: 'apse' }),
  P('pew-n0', 'low', '700,2750 745,2750 745,2910 700,2910', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-s0', 'low', '770,3090 815,3090 815,3250 770,3250', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-n1', 'low', '850,2750 895,2750 895,2910 850,2910', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-s1', 'low', '920,3090 965,3090 965,3250 920,3250', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-s2', 'low', '1070,3090 1115,3090 1115,3250 1070,3250', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-n3', 'low', '1150,2750 1195,2750 1195,2910 1150,2910', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-s3', 'low', '1220,3090 1265,3090 1265,3250 1220,3250', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-n4', 'low', '1300,2750 1345,2750 1345,2910 1300,2910', { height: 12, blocksBullets: false, blocksSight: false }),
  P('pew-s4', 'low', '1370,3090 1415,3090 1415,3250 1370,3250', { height: 12, blocksBullets: false, blocksSight: false }),
  P('tower', 'ruin', '420,3350 640,3350 640,3570 420,3570', { height: 70, group: 'tower2' }),
  P('rim-e0', 'rim', '3577,2653 3587,2716 3607,2772 3650,2821 3633,2886 3656,2943 3520,2954 3515,2907 3492,2864 3490,2816 3468,2774 3454,2727', { height: 34, group: 'crater' }),
  P('rim-e1', 'rim', '3637,3056 3669,3120 3622,3171 3631,3237 3594,3287 3578,3348 3438,3263 3464,3224 3476,3179 3509,3140 3513,3092 3510,3045', { height: 34, group: 'crater' }),
  P('rim-s0', 'rim', '3333,3554 3279,3578 3228,3605 3176,3639 3116,3647 3058,3666 3044,3506 3094,3521 3142,3514 3185,3493 3229,3474 3273,3454', { height: 34, group: 'crater' }),
  P('rim-s1', 'rim', '2944,3640 2883,3648 2830,3618 2763,3629 2712,3596 2669,3551 2733,3444 2770,3475 2816,3488 2865,3491 2906,3524 2956,3506', { height: 34, group: 'crater' }),
  P('pit-chunk-a', 'rim', '3328,2780 3294,2810 3250,2802 3228,2762 3246,2721 3289,2709 3326,2735', { height: 30, group: 'crater' }),
  wreck('ap1', 'car', 2780, 1560, 0.15),
  wreck('ap2', 'car', 3050, 1930, -0.6),
  P('lamp-fallen', 'low', '3344,1540 3359,1554 3355,1573 3336,1580 3056,1520 3041,1506 3045,1487 3064,1480', { height: 10, blocksBullets: false, blocksSight: false }),
  wreck('chk1', 'car', 4180, 2180, 1.9),
  wreck('truck', 'truck', 1900, 290, 0.2),
  P('fb0', 'barrel', '257,250 252,266 238,276 222,276 208,266 203,250 208,234 222,224 238,224 252,234', { height: 34, group: 'fb0' }),
  P('fb1', 'barrel', '2567,1340 2562,1356 2548,1366 2532,1366 2518,1356 2513,1340 2518,1324 2532,1314 2548,1314 2562,1324', { height: 34, group: 'fb1' }),
  P('fb2', 'barrel', '4977,820 4972,836 4958,846 4942,846 4928,836 4923,820 4928,804 4942,794 4958,794 4972,804', { height: 34, group: 'fb2' }),
  P('fb3', 'barrel', '5327,1300 5322,1316 5308,1326 5292,1326 5278,1316 5273,1300 5278,1284 5292,1274 5308,1274 5322,1284', { height: 34, group: 'fb3' }),
  P('fb4', 'barrel', '1027,1760 1022,1776 1008,1786 992,1786 978,1776 973,1760 978,1744 992,1734 1008,1734 1022,1744', { height: 34, group: 'fb4' }),
  P('fb5', 'barrel', '3927,1500 3922,1516 3908,1526 3892,1526 3878,1516 3873,1500 3878,1484 3892,1474 3908,1474 3922,1484', { height: 34, group: 'fb5' }),
  P('pump-post', 'post', '4042,1880 4036,1896 4020,1902 4004,1896 3998,1880 4004,1864 4020,1858 4036,1864', { height: 44 }),
];

const DOORS: readonly MapDoor[] = [
  { id: 'gs-store-e', kind: 'slide', x: 2625, y: 850, w: 100, axis: 'v', material: 'glass', glow: '#ffd9a0' },
  { id: 'gs-store-n', kind: 'swing', x: 2350, y: 725, w: 100, axis: 'h', material: 'metal' },
  { id: 'gs-wash-w', kind: 'slide', x: 3525, y: 850, w: 150, axis: 'v', material: 'metal' },
  { id: 'gs-wash-e', kind: 'slide', x: 3925, y: 850, w: 150, axis: 'v', material: 'metal' },
  { id: 'sh1-s', kind: 'swing', x: 4350, y: 625, w: 100, axis: 'h', material: 'tin' },
  { id: 'sh1-e', kind: 'swing', x: 4675, y: 300, w: 100, axis: 'v', material: 'tin' },
  { id: 'sh2-s', kind: 'double-swing', x: 4950, y: 475, w: 150, axis: 'h', material: 'tin' },
  { id: 'sh3-w', kind: 'swing', x: 5375, y: 450, w: 100, axis: 'v', material: 'tin' },
  { id: 'sh3-s', kind: 'slide', x: 5550, y: 625, w: 150, axis: 'h', material: 'tin', auto: false },
  { id: 'sh4-e', kind: 'swing', x: 4625, y: 950, w: 100, axis: 'v', material: 'tin' },
  { id: 'sh5-w', kind: 'swing', x: 5425, y: 950, w: 100, axis: 'v', material: 'tin' },
  { id: 'sh5-n', kind: 'swing', x: 5600, y: 825, w: 100, axis: 'h', material: 'tin' },
  { id: 'sh6-n', kind: 'swing', x: 4450, y: 1425, w: 100, axis: 'h', material: 'tin' },
  { id: 'sh6-gate', kind: 'swing', x: 4650, y: 1425, w: 100, axis: 'h', material: 'tin' },
  { id: 'sh7-n', kind: 'double-swing', x: 5150, y: 1425, w: 150, axis: 'h', material: 'tin' },
  { id: 'sh7-w', kind: 'swing', x: 4975, y: 1550, w: 100, axis: 'v', material: 'tin' },
  { id: 'sh8-n', kind: 'swing', x: 5650, y: 1475, w: 100, axis: 'h', material: 'tin' },
  { id: 'vestry-n', kind: 'swing', x: 1050, y: 2325, w: 100, axis: 'h', material: 'wood' },
  { id: 'hall-w', kind: 'double-swing', x: 525, y: 2900, w: 200, axis: 'v', material: 'wood' },
  { id: 'hall-n', kind: 'swing', x: 950, y: 2725, w: 100, axis: 'h', material: 'wood' },
  { id: 'hall-s', kind: 'swing', x: 1200, y: 3325, w: 100, axis: 'h', material: 'wood' },
  { id: 'av-c-s', kind: 'swing', x: 3500, y: 1725, w: 100, axis: 'h', material: 'wood' },
  { id: 'av-d-w', kind: 'swing', x: 3325, y: 2000, w: 100, axis: 'v', material: 'wood' },
  { id: 'ph-a', kind: 'swing', x: 550, y: 4075, w: 100, axis: 'h', material: 'wood' },
  { id: 'ph-b', kind: 'swing', x: 1325, y: 4050, w: 100, axis: 'v', material: 'wood' },
];

const ROOFS: readonly MapRoof[] = [
  roof('spanA-roof', '450,1370 340,1409 300,1455 300,1545 340,1591 450,1630 1130,1630 1130,1370', 'deck'),
  roof('spanB-roof', '1540,1370 1540,1630 2150,1630 2220,1572 2300,1540 2300,1460 2220,1428 2150,1370', 'deck'),
  roof('redcamp-tarp', '150,150 450,150 450,350 150,350', 'tarp'),
  roof('gs-store', '2150,700 2650,700 2650,1100 2150,1100', 'tin'),
  roof('gs-canopy', '2650,760 3180,760 3260,900 3450,960 3450,1120 2650,1120', 'canopy'),
  roof('gs-wash', '3500,800 3950,800 3950,1050 3500,1050', 'tin'),
  roof('sh1', '4200,200 4700,200 4700,650 4200,650', 'tin'),
  roof('sh2', '4800,150 5250,150 5250,500 4800,500', 'tin'),
  roof('sh3', '5350,200 5850,200 5850,650 5350,650', 'tin'),
  roof('sh4', '4200,750 4650,750 4650,1250 4200,1250', 'tin'),
  roof('sh5', '5400,800 5850,800 5850,1300 5400,1300', 'tin'),
  roof('sh6', '4250,1400 4800,1400 4800,1700 4250,1700', 'tin'),
  roof('sh7', '4950,1400 5450,1400 5450,1750 4950,1750', 'tin'),
  roof('sh8', '5550,1450 5850,1450 5850,1750 5550,1750', 'tin'),
  roof('sh-alley', '4700,700 4850,700 4850,1200 4700,1200', 'sheet'),
  roof('vestry', '850,2300 1350,2300 1350,2700 850,2700', 'slate'),
  roof('hall', '500,2700 1650,2700 1650,3350 500,3350', 'slate'),
  roof('av-d', '3300,1900 3750,1900 3750,2250 3300,2250', 'tin'),
];

export const WASTELAND = withGeometry(gridMap('Wasteland', WEST, 'wasteland'), { polys: POLYS, doors: DOORS, roofs: ROOFS });
